import React from "react";
import { useTranslation } from "react-i18next";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface DropdownOption {
  value: string;
  label: string;
  disabled?: boolean;
}

interface CustomSelectProps {
  value: string;
  onChange: (value: string) => void;
  options: DropdownOption[];
  style?: React.CSSProperties;
  className?: string;
  placeholder?: string;
  accentColor?: string;
  accentBgColor?: string;
  disabled?: boolean;
  "data-testid"?: string;
}

export const CustomSelect: React.FC<CustomSelectProps> = ({
  value,
  onChange,
  options,
  style,
  className,
  placeholder,
  disabled = false,
  "data-testid": dataTestId,
}) => {
  const { t } = useTranslation();
  const resolvedPlaceholder = placeholder ?? t("common.select.placeholder");

  return (
    <Select
      value={value}
      onValueChange={(val) => {
        if (val !== null) onChange(val);
      }}
      disabled={disabled}
    >
      <SelectTrigger className={className} style={style} data-testid={dataTestId}>
        <SelectValue placeholder={resolvedPlaceholder} />
      </SelectTrigger>
      <SelectContent
        alignItemWithTrigger={false}
        sideOffset={4}
        className="bg-[#1a1a1a] border-[#333333] text-white/80 rounded-md shadow-lg shadow-black/60 p-0 overflow-hidden backdrop-blur-md"
      >
        {options.length === 0 ? (
          <div className="px-2 py-1.5 text-xs text-muted-foreground text-center">
            {t("common.select.noOptions")}
          </div>
        ) : (
          options.map((option) => (
            <SelectItem
              key={option.value}
              value={option.value}
              disabled={option.disabled}
              className={`p-3 border-b border-white/5 cursor-pointer uppercase text-[0.85rem] focus:bg-[#2d6ff7]/15 focus:text-[#2d6ff7] active:bg-[#2d6ff7]/15 transition-colors ${
                value === option.value
                  ? "bg-[#2d6ff7]/15 text-[#2d6ff7] font-bold"
                  : "bg-transparent text-[#ccc] font-normal"
              }`}
              data-testid={
                dataTestId
                  ? `${dataTestId}-option-${option.value.toLowerCase().replace(/\s+/g, "-")}`
                  : undefined
              }
            >
              {option.label}
            </SelectItem>
          ))
        )}
      </SelectContent>
    </Select>
  );
};
